import { TimeRange, WeekDays } from "common/Calendars/types.ts";
import {
  MAX_DAY_END_TIME,
  NIGHT_HOURS,
  WEEK_DAY_TO_DISPLAY_NAME,
  WEEK_DAYS,
  WEEKEND_DAYS,
} from "common/Calendars/constants.ts";
import { isArray } from "lodash";
import { BlacklistedOperationHours, OperationHoursEvent } from "@shared/types";
import moment from "moment-timezone";
import { DAYS_IN_WEEK } from "@shared/common/constants.ts";
import dayjs, { Dayjs } from "dayjs";

export const getBaseDate = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = now.getDate() - dayOfWeek;
  const baseDate = new Date(now.setDate(diff));
  baseDate.setHours(0, 0, 0, 0);
  return baseDate;
};

export const getEventId = (start: Date, end: Date) => {
  return `${
    WEEK_DAY_TO_DISPLAY_NAME[start?.getDay()]
  }-${start?.getHours()}:${start?.getMinutes()}-${end?.getHours()}:${end?.getMinutes()}`;
};

const isStartOfDay = (date?: Date) => {
  return date?.getHours() === 0 && date?.getMinutes() === 0;
};

export const isEndOfDay = (date?: Date) => {
  return (
    date?.getHours() === MAX_DAY_END_TIME[0] &&
    date?.getMinutes() === MAX_DAY_END_TIME[1]
  );
};

export const getIsAllDayEvent = (event: OperationHoursEvent) => {
  return isStartOfDay(event.start) && isEndOfDay(event.end);
};

export const getWeekDates = (baseDate: Date): Date[] => {
  const start = new Date(baseDate);
  start.setHours(0, 0, 0, 0);
  const week: Date[] = [];

  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    week.push(day);
  }
  return week;
};

export const getActiveTime = (
  events: OperationHoursEvent[]
): OperationHoursEvent[] => {
  const activeEvents: OperationHoursEvent[] = [];
  const weekDates = getWeekDates(getBaseDate());

  for (const day of weekDates) {
    const dayStart = new Date(day);
    const dayEnd = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    dayEnd.setHours(...MAX_DAY_END_TIME);

    const inactiveDayEvents = events
      .filter(
        (event) =>
          !event.isActive && event.start?.toDateString() === day.toDateString()
      )
      .sort((a, b) => (a.start?.getTime() || 0) - (b.start?.getTime() || 0));

    let cursor = new Date(dayStart);

    for (const event of inactiveDayEvents) {
      if (event.start && cursor < event.start) {
        const start = new Date(cursor);
        const end = new Date(event.start);
        activeEvents.push({
          id: getEventId(start, end),
          start,
          end,
          isActive: true,
        });
      }
      cursor = new Date(Math.max(cursor.getTime(), event.end?.getTime() || 0));
    }

    if (cursor < dayEnd) {
      const start = new Date(cursor);
      const end = new Date(dayEnd);
      activeEvents.push({
        id: getEventId(start, end),
        start,
        end,
        isActive: true,
      });
    }
  }

  return activeEvents;
};

export const mergeInactiveEvents = (events: OperationHoursEvent[]) => {
  const activeEvents = events.filter((e) => e.isActive);
  const inactiveEvents = events
    .filter((e) => !e.isActive)
    .slice()
    .sort((a, b) => (a.start?.getTime() || 0) - (b.start?.getTime() || 0));

  if (!inactiveEvents.length) return events;

  const merged: OperationHoursEvent[] = [inactiveEvents[0]];
  for (const event of inactiveEvents) {
    const last = merged[merged.length - 1];
    if (last.end && last.start && event.start && last.end >= event.start) {
      const end = new Date(
        Math.max(last.end.getTime(), event.end?.getTime() || 0)
      );
      last.end = end;
      last.id = getEventId(last.start, end);
    } else {
      merged.push({ ...event });
    }
  }
  return [...activeEvents, ...merged];
};

const shouldShiftDates = (start: Date, end: Date) => {
  const shouldShiftDays =
    start.getDay() === WeekDays.SATURDAY && end.getDay() === WeekDays.SUNDAY;
  const shouldShiftStart = shouldShiftDays && start < getBaseDate();
  const shouldShiftEnd =
    shouldShiftDays && end > getDateWithOffset(WeekDays.SATURDAY + 1);

  return { shouldShiftStart, shouldShiftEnd };
};

const shiftDateInAWeek = (date: Date, add?: boolean) =>
  date.setDate(date.getDate() + (add ? 1 : -1) * DAYS_IN_WEEK);

export const splitEventAtMidnight = (
  event: OperationHoursEvent,
  start: Date,
  end: Date
) => {
  const events: OperationHoursEvent[] = [];
  const startDate = new Date(start);
  const endDate = new Date(end);

  if (endDate.getDate() !== startDate.getDate()) {
    const { shouldShiftStart, shouldShiftEnd } = shouldShiftDates(
      startDate,
      endDate
    );
    const firstPartDate = new Date(startDate);
    if (shouldShiftStart) shiftDateInAWeek(firstPartDate, true);
    const firstPartEnd = new Date(firstPartDate);
    firstPartEnd.setHours(...MAX_DAY_END_TIME);
    events.push({
      ...event,
      id: getEventId(firstPartDate, firstPartEnd),
      start: firstPartDate,
      end: firstPartEnd,
    });

    const secondPartDate = new Date(endDate);
    if (shouldShiftEnd) shiftDateInAWeek(secondPartDate, false);
    const secondPartStart = new Date(secondPartDate);
    secondPartStart.setHours(0, 0, 0, 0);
    events.push({
      ...event,
      id: getEventId(secondPartStart, secondPartDate),
      start: secondPartStart,
      end: secondPartDate,
    });
  } else {
    const updatedStart = new Date(startDate);
    const updatedEnd = new Date(endDate);
    if (start < getBaseDate()) {
      shiftDateInAWeek(updatedStart, true);
      shiftDateInAWeek(updatedEnd, true);
    } else if (start > getDateWithOffset(WeekDays.SATURDAY + 1)) {
      shiftDateInAWeek(updatedStart, false);
      shiftDateInAWeek(updatedEnd, false);
    }
    events.push({
      ...event,
      id: getEventId(updatedStart, updatedEnd),
      start: updatedStart,
      end: updatedEnd,
    });
  }
  return events;
};

export const getInactiveDays = (events: OperationHoursEvent[]) => {
  return new Set(
    events
      .filter((e) => {
        if (e.isActive || !e.start || !e.end) return false;
        return getIsAllDayEvent(e);
      })
      .map((e) => e.start?.getDay())
  );
};

export const isInactiveAllDayEvent = (
  e: OperationHoursEvent,
  dayIndex: number | number[]
) => {
  const days = isArray(dayIndex) ? dayIndex : [dayIndex];
  return (
    !e.isActive &&
    e.start &&
    days.includes(e.start.getDay()) &&
    getIsAllDayEvent(e)
  );
};

export const changeTime = (date: Date, hour = 0, minute = 0) => {
  const newDate = new Date(date);
  newDate.setHours(hour, minute, 0, 0);
  return newDate;
};

export const getDateWithOffset = (
  offset: number,
  hour = 0,
  minute = 0,
  second = 0,
  ms = 0
) => {
  const baseDate = getBaseDate();
  const date = new Date(baseDate);
  date.setDate(baseDate.getDate() + offset);
  date.setHours(hour, minute, second, ms);
  return date;
};

export const mergeDayEventsIntoFullDay = (
  events: OperationHoursEvent[],
  dayIndex: WeekDays
): OperationHoursEvent[] => {
  const dayStart = getDateWithOffset(dayIndex, 0, 0);
  const dayEnd = getDateWithOffset(dayIndex, ...MAX_DAY_END_TIME);

  const remainingEvents = events.filter((e) => {
    return e.start?.getDay() !== dayIndex;
  });

  const newEvent: OperationHoursEvent = {
    id: getEventId(dayStart, dayEnd),
    start: dayStart,
    end: dayEnd,
    isActive: false,
  };

  return [...remainingEvents, newEvent];
};

export const getIsWeekendDisabled = (events: OperationHoursEvent[]) => {
  return WEEKEND_DAYS.every((day) =>
    events.some((event) => isInactiveAllDayEvent(event, day))
  );
};

export const getAllDayEvent = (dayIndex: WeekDays) => {
  const start = getDateWithOffset(dayIndex);
  const end = getDateWithOffset(dayIndex, ...MAX_DAY_END_TIME);
  return {
    id: getEventId(start, end),
    start,
    end,
    isActive: false,
  };
};

export const isNightDisabledForDay = (
  events: OperationHoursEvent[],
  dayIndex: number
) => {
  const nightStart = getDateWithOffset(dayIndex, NIGHT_HOURS[0]);
  const nightEnd = getDateWithOffset(dayIndex, ...MAX_DAY_END_TIME);
  const morningStart = getDateWithOffset(dayIndex, 0);
  const morningEnd = getDateWithOffset(dayIndex, NIGHT_HOURS[1]);

  const isCovered = (rangeStart: Date, rangeEnd: Date) => {
    const relevantEvents = events
      .filter((e) => !e.isActive)
      .filter(
        (e) => e.start && e.end && e.end > rangeStart && e.start < rangeEnd
      )
      .sort((a, b) => (a.start?.getTime() || 0) - (b.start?.getTime() || 0));

    if (relevantEvents.length === 0) return false;

    let current = new Date(rangeStart);
    for (const e of relevantEvents) {
      if (e.start && e.start > current) return false;
      current = new Date(Math.max(current.getTime(), e.end?.getTime() || 0));
      if (current >= rangeEnd) return true;
    }
    return current >= rangeEnd;
  };

  return isCovered(nightStart, nightEnd) && isCovered(morningStart, morningEnd);
};

export const getIsNightDisabled = (events: OperationHoursEvent[]) => {
  return WEEK_DAYS.every((day) => isNightDisabledForDay(events, day));
};

export const isNightEvent = (e: OperationHoursEvent) => {
  return (
    (e.start?.getHours() === NIGHT_HOURS[0] &&
      e.start?.getMinutes() === 0 &&
      isEndOfDay(e.end)) ||
    (isStartOfDay(e.start) &&
      e.end?.getHours() === NIGHT_HOURS[1] &&
      e.end.getMinutes() === 0)
  );
};

export const convertTZ = (
  date: Date,
  prevTimezone: string,
  timezone: string,
  type: "start" | "end"
) => {
  if (!prevTimezone) return date;

  const prevOffset = moment.tz(prevTimezone).utcOffset();
  const newOffset = moment.tz(timezone).utcOffset();
  const diffMinutes = newOffset - prevOffset;

  const convertedDate = new Date(date);
  convertedDate.setMinutes(convertedDate.getMinutes() + diffMinutes);
  return roundTo15Min(convertedDate, type);
};

export const getDisabledWeekHoursEvents = (
  value: [Dayjs | null, Dayjs | null]
) => {
  const events: OperationHoursEvent[] = [];
  const weekDates = getWeekDates(getBaseDate());

  weekDates.forEach((date) => {
    const [startDayjs, endDayjs] = value;
    const start = startDayjs?.toDate();
    const end = endDayjs?.toDate();
    start?.setDate(date.getDate());
    end?.setDate(date.getDate());
    if (isEndOfDay(end)) {
      end?.setHours(...MAX_DAY_END_TIME);
    }

    if (!start || !end) return;

    if (end.getTime() < start.getTime()) {
      const endOfDay = new Date(start);
      endOfDay.setHours(...MAX_DAY_END_TIME);
      const firstEvent = {
        isActive: false,
        start,
        end: endOfDay,
        id: getEventId(start, endOfDay),
      };
      events.push(firstEvent);

      const startOfDay = new Date(end);
      startOfDay.setHours(0, 0, 0, 0);
      const secondEvent = {
        isActive: false,
        start: startOfDay,
        end,
        id: getEventId(startOfDay, end),
      };
      events.push(secondEvent);
    } else {
      const newEvent = {
        isActive: false,
        start,
        end,
        id: getEventId(start, end),
      };
      events.push(newEvent);
    }
  });
  return events;
};

export const getDailyRanges = (
  events: OperationHoursEvent[]
): Record<number, TimeRange[]> => {
  const dailyRanges: Record<number, TimeRange[]> = {};

  events.forEach((event) => {
    if (!event.start || !event.end) return;
    const day = event.start.getDay();
    if (!dailyRanges[day]) dailyRanges[day] = [];
    dailyRanges[day].push({ start: event.start, end: event.end });
  });

  return dailyRanges;
};

const toMinutes = (date: Date) => {
  return date.getHours() * 60 + date.getMinutes();
};

const intersectRanges = (
  rangesA: TimeRange[],
  rangesB: TimeRange[]
): TimeRange[] => {
  const result: TimeRange[] = [];
  for (const a of rangesA) {
    const aStart = toMinutes(a.start);
    const aEnd = toMinutes(a.end);

    for (const b of rangesB) {
      const bStart = toMinutes(b.start);
      const bEnd = toMinutes(b.end);

      const startMin = Math.max(aStart, bStart);
      const endMin = Math.min(aEnd, bEnd);

      if (startMin < endMin) {
        const start = new Date();
        start.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
        const end = new Date();
        end.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0);
        if (isEndOfDay(end)) {
          end.setHours(...MAX_DAY_END_TIME);
        }
        result.push({ start, end });
      }
    }
  }
  return result;
};

export const findCommonTimeRangeAcrossWeek = (
  dailyRanges: Record<number, TimeRange[]>
) => {
  const days = Object.keys(dailyRanges).map(Number);
  if (days.length < 7) return [];

  let commonRanges = dailyRanges[days[0]];
  for (let i = 1; i < days.length; i++) {
    commonRanges = intersectRanges(commonRanges, dailyRanges[days[i]]);
    if (!commonRanges.length) break;
  }
  return commonRanges;
};

export const removeEventsByDisabledHours = (
  removedRange: [Dayjs | null, Dayjs | null],
  allEvents: OperationHoursEvent[]
): OperationHoursEvent[] => {
  const [startTime, endTime] = removedRange;
  if (!startTime || !endTime) return allEvents;

  const disabledStartMin = startTime.hour() * 60 + startTime.minute();
  const disabledEndMin = endTime.hour() * 60 + endTime.minute();
  const crossesMidnight = disabledEndMin < disabledStartMin;

  const updatedEvents: OperationHoursEvent[] = [];

  for (const event of allEvents) {
    if (!event.start || !event.end) {
      updatedEvents.push(event);
      continue;
    }
    const start = new Date(event.start);
    const end = new Date(event.end);
    const startMin = toMinutes(start);
    const endMin = toMinutes(end);

    if (crossesMidnight) {
      if (endMin <= disabledStartMin && startMin >= disabledEndMin) {
        updatedEvents.push(event);
        continue;
      }
      if (
        (startMin >= disabledStartMin || startMin <= disabledEndMin) &&
        (endMin <= disabledEndMin || endMin >= disabledStartMin)
      ) {
        if (startMin < disabledStartMin && endMin > disabledEndMin) {
          const newEnd = new Date(start);
          newEnd.setHours(startTime.hour(), startTime.minute(), 0, 0);
          updatedEvents.push({
            ...event,
            id: getEventId(start, newEnd),
            start,
            end: newEnd,
          });

          const newStart = new Date(end);
          newStart.setHours(endTime.hour(), endTime.minute(), 0, 0);
          updatedEvents.push({
            ...event,
            id: getEventId(newStart, end),
            start: newStart,
            end,
          });
        }
        continue;
      }
      if (startMin < disabledStartMin && endMin >= disabledStartMin) {
        const newEnd = new Date(start);
        newEnd.setHours(startTime.hour(), startTime.minute(), 0, 0);
        updatedEvents.push({
          ...event,
          id: getEventId(start, newEnd),
          start,
          end: newEnd,
        });
      }
      if (startMin <= disabledEndMin && endMin > disabledEndMin) {
        const newStart = new Date(end);
        newStart.setHours(endTime.hour(), endTime.minute(), 0, 0);
        updatedEvents.push({
          ...event,
          id: getEventId(newStart, end),
          start: newStart,
          end,
        });
      }
    } else {
      if (endMin <= disabledStartMin || startMin >= disabledEndMin) {
        updatedEvents.push(event);
        continue;
      }
      if (startMin >= disabledStartMin && endMin <= disabledEndMin) {
        continue;
      }
      if (startMin < disabledStartMin) {
        const newEnd = new Date(start);
        newEnd.setHours(startTime.hour(), startTime.minute(), 0, 0);
        updatedEvents.push({
          ...event,
          id: getEventId(start, newEnd),
          start,
          end: newEnd,
        });
      }
      if (endMin > disabledEndMin) {
        const newStart = new Date(end);
        newStart.setHours(endTime.hour(), endTime.minute(), 0, 0);
        updatedEvents.push({
          ...event,
          id: getEventId(newStart, end),
          start: newStart,
          end,
        });
      }
    }
  }
  return updatedEvents;
};

export const roundTo15Min = (date: Date, type: "start" | "end") => {
  const newDate = new Date(date);
  const minutes = newDate.getMinutes();
  const rounded =
    type === "start"
      ? Math.floor(minutes / 15) * 15
      : Math.ceil(minutes / 15) * 15;

  newDate.setMinutes(rounded, 0, 0);
  return newDate;
};

export const mergeTimeRangesAcrossMidnight = (
  ranges: TimeRange[]
): TimeRange[] => {
  let updatedRanges: TimeRange[] = [...ranges];
  const endsAtMidnight = ranges.find((range) => isEndOfDay(range.end));
  const startAtMidnight = ranges.find((range) => isStartOfDay(range.start));
  if (!!endsAtMidnight && !!startAtMidnight) {
    updatedRanges.push({
      start: endsAtMidnight.start,
      end: startAtMidnight.end,
    });
    updatedRanges = updatedRanges.filter(
      (range) => range !== endsAtMidnight && range !== startAtMidnight
    );
  }
  return updatedRanges;
};

const toTimeString = (date?: Date) => {
  let format = "HH:mm";
  if (isEndOfDay(date)) format = "HH:mm:ss.SSSSSS";
  return dayjs(date).format(format);
};

export const getParsedOperationHoursEvents = (
  events: OperationHoursEvent[] | undefined,
  timezone: string
) => {
  return events?.map((event) => {
    return {
      day: event.start?.getDay(),
      start_hour: toTimeString(event.start),
      end_hour: toTimeString(event.end),
      timezone: timezone,
    };
  }) as BlacklistedOperationHours[];
};

export const convertToLocalEvents = (events: BlacklistedOperationHours[]) => {
  return events.map((event) => {
    const [startHour, startMinutes] = event.start_hour.split(":");
    const [endHour, endMinutes] = event.end_hour.split(":");
    const isEndOfDay =
      Number(endHour) === MAX_DAY_END_TIME[0] &&
      Number(endMinutes) === MAX_DAY_END_TIME[1];
    let endSeconds = isEndOfDay ? MAX_DAY_END_TIME[2] : 0;
    let endMs = isEndOfDay ? MAX_DAY_END_TIME[3] : 0;

    const start = getDateWithOffset(
      event.day,
      Number(startHour),
      Number(startMinutes)
    );
    const end = getDateWithOffset(
      event.day,
      Number(endHour),
      Number(endMinutes),
      endSeconds,
      endMs
    );

    return {
      start,
      end,
      id: getEventId(start, end),
      isActive: false,
    };
  });
};
